'use client';

import { useEffect, useRef, useState } from 'react';

import { assertUnreachable } from '@bridge/utils';

import type { DataTableActiveRow } from '../data-table/types';
import type { GetRowId } from './rowIdentity';

/**
 * Enables a single "currently being viewed" row on a list or table, the
 * master/detail counterpart to multi-row selection. The active row is keyed by
 * the table's row id, never by index.
 */
export interface ActiveRowConfig {
  /** Auto-activate the first row when nothing is active yet. Defaults to 'none'. */
  initial?: 'first' | 'none';
}

/** `ActiveRowConfig` plus the wiring the table supplies: shared row identity and change reporting. */
export interface ResolvedActiveRowConfig<TRow> extends ActiveRowConfig {
  getRowId: GetRowId<TRow>;
  /** Reports the resolved active row: on activation, and on any data change that replaces or drops it. */
  onChange?: (row: TRow | null) => void;
}

/**
 * Imperative handle for tables built at module scope, which cannot hand a setter
 * back to the call site the way `useDataTable` returns `setActiveRow`.
 */
export interface ActiveRowHandle {
  /** Drop the active row and its highlight, reporting `null` through `onChange`. */
  clear: () => void;
}

export interface UseActiveRowResult<TRow> {
  enabled: boolean;
  activeRowId: string | null;
  /** The active row resolved against the CURRENT data, or null. */
  activeRow: TRow | null;
  setActiveRow: (row: TRow | null) => void;
  isActive: (row: TRow) => boolean;
  /** Ready for `<DataTable activeRowState>` / `<DataTableBody activeRowState>`. */
  tableState: DataTableActiveRow<TRow>;
}

/** What the user has done, which outranks the `initial` config. */
type ActiveRowChoice = { type: 'untouched' } | { type: 'cleared' } | { type: 'row'; id: string };

const UNTOUCHED: ActiveRowChoice = { type: 'untouched' };

/**
 * Owns the active-row state for master/detail views: the id of the active row,
 * its resolution against the latest `data`, and the optional auto-activation of
 * the first row.
 *
 * Only the id is stored, so a refetch that returns a new object for the same
 * row keeps that row active, while a row that leaves `data` (deleted, filtered
 * out, paged away) resolves `activeRow` to null without any call-site bookkeeping.
 *
 * With `initial: 'first'` the first row is active from the first paint, so a
 * detail pane driven off it never renders empty. It reactivates whenever the
 * active row is no longer in `data` (a page or filter change lands on the new
 * first row), but an explicit clear to null wins until the user activates
 * something again.
 */
export function useActiveRow<TRow>(
  config: ResolvedActiveRowConfig<TRow> | undefined,
  data: TRow[]
): UseActiveRowResult<TRow> {
  const [choice, setChoice] = useState<ActiveRowChoice>(UNTOUCHED);

  const enabled = !!config;
  const wantsInitialFirst = config?.initial === 'first';

  const firstRow = data[0];
  const firstRowId = config && firstRow ? config.getRowId(firstRow) : null;
  const chosenIsPresent =
    !!config && choice.type === 'row' && data.some((row) => config.getRowId(row) === choice.id);

  // Derived during render so the first paint already has an active row.
  const activeRowId = resolveActiveRowId({ choice, chosenIsPresent, wantsInitialFirst, firstRowId });

  const setActiveRow = (row: TRow | null) => {
    if (!config) return;

    setChoice(row ? { type: 'row', id: config.getRowId(row) } : { type: 'cleared' });
  };

  const isActive = (row: TRow) => !!config && activeRowId !== null && config.getRowId(row) === activeRowId;

  const activeRow =
    config && activeRowId !== null ? (data.find((row) => config.getRowId(row) === activeRowId) ?? null) : null;

  // The resolved row, not the click, is what `onChange` reports: a refetch that drops the
  // active row or returns a fresh object for it has to reach the caller too, and callers
  // that only get the row through this callback have no other way to hear about it.
  const notifiedRow = useRef<TRow | null>(null);
  useEffect(() => {
    if (!config || notifiedRow.current === activeRow) return;

    notifiedRow.current = activeRow;
    config.onChange?.(activeRow);
  }, [activeRow]);

  return {
    enabled,
    activeRowId,
    activeRow,
    setActiveRow,
    isActive,
    tableState: { activeRowId, isActive, setActiveRow },
  };
}

function resolveActiveRowId({
  choice,
  chosenIsPresent,
  wantsInitialFirst,
  firstRowId,
}: {
  choice: ActiveRowChoice;
  chosenIsPresent: boolean;
  wantsInitialFirst: boolean;
  firstRowId: string | null;
}): string | null {
  switch (choice.type) {
    case 'cleared':
      return null;
    case 'untouched':
      return wantsInitialFirst ? firstRowId : null;
    case 'row':
      // A chosen row missing from `data` keeps its id under `initial: 'none'`, so a caller
      // reading `activeRowId` can still tell which row the user picked before it vanished.
      return !chosenIsPresent && wantsInitialFirst ? firstRowId : choice.id;
    default:
      return assertUnreachable(choice);
  }
}
